/* eslint no-useless-return: "error" */
import _ from 'lodash';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Mentions,
} from 'antd';

import InputFile from '../shared/InputFile';
import ChannelAPIConnectors from '../../api/ChannelAPIConnectors';
import MedicalRecordPreview from './MedicalRecordPreview';
import i18n from '../../config/i18n';
import {
  USER_TAG_PREFIX,
} from '../../modules/messaging/costants';
import '../../styles/css/CreateMessageForm.css';


const CreateMessageForm = ({
  actions,
  actions: {
    createWarningAlert,
  },
  channelMembers,
  className,
  currentChannelId,
  enableMessaging,
  onCreateImageMessage,
  onCreateTextMessage,
  onTagMedicalRecord,
  medicalRecords,
  patientDetail,
}) => {
  const [memberOptions, setMemberOptions] = useState([]);
  const [medicalRecordOptions, setMedicalRecordOptions] = useState([]);
  const [input, setInput] = useState('');
  const [mentionPrefix, setMentionPrefix] = useState(USER_TAG_PREFIX);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const uploadImageRef = useRef();
  const listenerRef = useRef();
  const inputTagLength = useRef(0);
  const memberIdsString = JSON.stringify(channelMembers.map(mem => mem.user.id));

  useEffect(() => {
    setMemberOptions(channelMembers.map(({ user: { firstName, lastName, id } }) => (
      <Mentions.Option
        key={id.toString()}
        userId={id}
        value={`{{${id}_${firstName}_${lastName}}}`}
      >
        {`${firstName} ${lastName}`}
      </Mentions.Option>
    )));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberIdsString]);

  useEffect(() => {
    setMedicalRecordOptions(medicalRecords.map(({ firstName, lastName, id }) => (
      <Mentions.Option
        key={id.toString()}
        medicalRecordId={id}
        value={`${firstName} ${lastName} ${id}`}
      >
        {`${firstName} ${lastName}`}
      </Mentions.Option>
    )));
  }, [medicalRecords]);

  const handleSendMessageClick = () => {
    const trimmedValue = input.trim();
    if (_.isEmpty(trimmedValue)) return;
    if (trimmedValue.length > 200) {
      createWarningAlert(i18n.t('msgTooLong'));
    } else {
      onCreateTextMessage(currentChannelId, trimmedValue);
      setInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) { // Press Enter
      e.preventDefault();
      handleSendMessageClick();
    }
  };

  // Update listener so that we can send message correctly by pressing 'Enter'.
  useEffect(() => {
    const mentions = listenerRef.current;
    mentions.addEventListener('keydown', handleKeyDown);
    return () => { mentions.removeEventListener('keydown', handleKeyDown); };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const createImage = (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      // upload photo
      const uploadPromise = uploadImageRef.current.uploadWrapper(
        ChannelAPIConnectors.createChannelImageMessage, currentChannelId);
      const msg = await uploadPromise;
      onCreateImageMessage({ channelId: currentChannelId, msg });
    };
    reader.readAsDataURL(file);
  };

  // trigger selecting file dialog
  const handleImageSelectAndUpload = (e) => {
    e.preventDefault();
    uploadImageRef.current.triggerClick();
    return false;
  };

  const handleSelectFile = (file) => {
    createImage(file);
  };

  // This function is called after the input change.
  // We setInput here to make the input not change.
  const handleSelectOption = (option) => {
    const medicalRecordId = option.medicalRecordId;
    if (medicalRecordId) {
      setInput(input);
      setIsAddingTag(true);
      onTagMedicalRecord(medicalRecordId);
    }
  };

  const handleCancelTag = () => {
    setIsAddingTag(false);
  };

  const handleConfirmTag = () => {
    setInput(`${input.substring(0, input.length - inputTagLength.current)}{{${patientDetail.id}_${patientDetail.firstName}_${patientDetail.lastName}}} `);
    setIsAddingTag(false);
  };

  const handleInputChange = (value) => {
    setInput(value);
  };

  const handleSearch = (text, prefix) => {
    if (mentionPrefix !== prefix) setMentionPrefix(prefix);
    inputTagLength.current = text.length;
  };

  return (
    <div
      className={`${className} create-message-form`}
    >
      <Icon
        className="upload-image-icon"
        onClick={handleImageSelectAndUpload}
        type="picture"
      />
      <InputFile
        actions={actions}
        className="file-selector"
        ref={uploadImageRef}
        onSelectFile={handleSelectFile}
      />
      <div
        className="mention-wrapper"
        ref={listenerRef}
      >
        <Mentions
          disabled={!enableMessaging}
          onChange={handleInputChange}
          onSearch={handleSearch}
          onSelect={handleSelectOption}
          placeholder={i18n.t(enableMessaging ? 'mentionTip' : 'noPermission')}
          placement="top"
          prefix={['@', '#']}
          value={input}
        >
          {
            mentionPrefix === USER_TAG_PREFIX ? memberOptions : medicalRecordOptions
          }
        </Mentions>
      </div>
      <Button
        className="send-message-button"
        onClick={handleSendMessageClick}
        shape="circle"
      >
        <i
          className="fas fa-paper-plane send-message-icon"
        />
      </Button>
      <MedicalRecordPreview
        cancelText={i18n.t('cancel')}
        okText={i18n.t('tag')}
        onCancel={handleCancelTag}
        onClose={handleCancelTag}
        onOk={handleConfirmTag}
        medicalRecordDetail={patientDetail}
        visible={isAddingTag}
      />
    </div>
  );
};

CreateMessageForm.propTypes = {
  actions: PropTypes.object,
  channelMembers: PropTypes.array,
  currentChannelId: PropTypes.number.isRequired,
  enableMessaging: PropTypes.bool,
  onCreateImageMessage: PropTypes.func.isRequired,
  onCreateTextMessage: PropTypes.func.isRequired,
};

CreateMessageForm.defaultProps = {
  actions: {},
  channelMembers: [],
  enableMessaging: true,
};


export default CreateMessageForm;



// WEBPACK FOOTER //
// ./src/components/messaging/CreateMessageForm.js